/**
 * Pure camera geometry for the Parcel Test-Fit Studio.
 *
 * The blueprint camera is stored in engineering coordinates (local metres),
 * not CSS pixels. That keeps the same physical framing through pane toggles,
 * full-screen transitions, and responsive resizes.
 */
import type {
  GenerationLayoutComponentPolygon,
  GenerationParcelTestFitResult,
} from '../../../../lib/generationImpact/generationLayoutGeometry';

export type StudioFitTarget =
  | 'layout'
  | 'target-parcel'
  | 'selection'
  | 'issues'
  /** Map-only target; blueprint deliberately falls back to its active parcel. */
  | 'all-parcels';

export interface StudioPoint2D {
  x: number;
  y: number;
}

export interface StudioBounds2D {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface BlueprintFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BlueprintCamera {
  /** Local-plan centre, metres east/right along the rotated layout x axis. */
  centerXM: number;
  /** Local-plan centre, metres up along the rotated layout y axis. */
  centerYM: number;
  /** Engineering scale, independent of viewport size. */
  metresPerPixel: number;
}

export interface StudioMapCamera {
  center: { lat: number; lng: number };
  zoom: number;
}

export interface StudioViewportSnapshot {
  blueprint?: BlueprintCamera;
  map?: StudioMapCamera;
}

const MIN_BOUNDS_SPAN_M = 1;
const DEFAULT_FIT_PADDING_PX = 40;

export function isStudioFitIssueComponent(
  component: GenerationLayoutComponentPolygon,
): boolean {
  return (component.exclusionOverlapAcres ?? 0) > 0.01
    || (component.obstacleOverlapAcres ?? 0) > 0.01
    || component.meetsParcelEdgeSetback === false
    || (component.outsideAcres ?? 0) > 0.01;
}

export function boundsFromLocalPoints(
  points: readonly { xM: number; yM: number }[],
): StudioBounds2D | null {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let found = false;
  for (const point of points) {
    if (!Number.isFinite(point.xM) || !Number.isFinite(point.yM)) continue;
    minX = Math.min(minX, point.xM);
    minY = Math.min(minY, point.yM);
    maxX = Math.max(maxX, point.xM);
    maxY = Math.max(maxY, point.yM);
    found = true;
  }
  return found ? { minX, minY, maxX, maxY } : null;
}

function requiredEnvelopeCorners(
  testFit: GenerationParcelTestFitResult,
): Array<{ xM: number; yM: number }> {
  const required = testFit.plan?.requiredEnvelope;
  if (!required) return [];
  return [
    { xM: -required.widthM / 2, yM: -required.heightM / 2 },
    { xM: required.widthM / 2, yM: -required.heightM / 2 },
    { xM: required.widthM / 2, yM: required.heightM / 2 },
    { xM: -required.widthM / 2, yM: required.heightM / 2 },
  ];
}

/**
 * Resolve a blueprint fit target. Missing selection/issues fall back to the
 * whole engineering layout so a fit command can never throw or blank the view.
 */
export function blueprintBoundsForTarget(
  testFit: GenerationParcelTestFitResult,
  target: StudioFitTarget,
  selectedId: string | null,
): StudioBounds2D | null {
  const parcelPoints = testFit.envelope?.parcelPolygonM ?? [];
  const componentPoints = testFit.components.flatMap(component => component.rectangle.polygonM);
  const layoutPoints = [
    ...parcelPoints,
    ...componentPoints,
    ...requiredEnvelopeCorners(testFit),
  ];

  if (target === 'target-parcel' || target === 'all-parcels') {
    return boundsFromLocalPoints(parcelPoints) ?? boundsFromLocalPoints(layoutPoints);
  }
  if (target === 'selection') {
    const selected = testFit.components.find(
      component => component.rectangle.id === selectedId,
    );
    return boundsFromLocalPoints(selected?.rectangle.polygonM ?? [])
      ?? boundsFromLocalPoints(layoutPoints);
  }
  if (target === 'issues') {
    const issuePoints = testFit.components
      .filter(isStudioFitIssueComponent)
      .flatMap(component => component.rectangle.polygonM);
    return boundsFromLocalPoints(issuePoints) ?? boundsFromLocalPoints(layoutPoints);
  }
  return boundsFromLocalPoints(layoutPoints);
}

/**
 * Fit engineering bounds into a responsive drawing frame.
 *
 * `metresPerPixel` is chosen from both horizontal and vertical spans, so the
 * entire target stays inside the frame regardless of its aspect ratio.
 */
export function fitBlueprintCamera(
  bounds: StudioBounds2D,
  frame: BlueprintFrame,
  paddingPx = DEFAULT_FIT_PADDING_PX,
): BlueprintCamera {
  const safeWidth = Math.max(1, frame.width);
  const safeHeight = Math.max(1, frame.height);
  const padding = Math.min(
    Math.min(safeWidth, safeHeight) * 0.45,
    Math.max(0, paddingPx),
  );
  const usableWidth = Math.max(1, safeWidth - padding * 2);
  const usableHeight = Math.max(1, safeHeight - padding * 2);
  const spanX = Math.max(MIN_BOUNDS_SPAN_M, bounds.maxX - bounds.minX);
  const spanY = Math.max(MIN_BOUNDS_SPAN_M, bounds.maxY - bounds.minY);
  const metresPerPixel = Math.max(
    spanX / usableWidth,
    spanY / usableHeight,
  );
  return {
    centerXM: (bounds.minX + bounds.maxX) / 2,
    centerYM: (bounds.minY + bounds.maxY) / 2,
    metresPerPixel,
  };
}

export function blueprintScale(
  camera: BlueprintCamera,
  frame: BlueprintFrame,
): number {
  return 1 / Math.max(1e-9, camera.metresPerPixel);
}

export function localToBlueprint(
  point: { xM: number; yM: number },
  camera: BlueprintCamera,
  frame: BlueprintFrame,
): StudioPoint2D {
  const scale = blueprintScale(camera, frame);
  return {
    x: frame.x + frame.width / 2 + (point.xM - camera.centerXM) * scale,
    y: frame.y + frame.height / 2 - (point.yM - camera.centerYM) * scale,
  };
}

export function blueprintToLocal(
  point: StudioPoint2D,
  camera: BlueprintCamera,
  frame: BlueprintFrame,
): { xM: number; yM: number } {
  const scale = blueprintScale(camera, frame);
  return {
    xM: camera.centerXM + (point.x - (frame.x + frame.width / 2)) / scale,
    yM: camera.centerYM - (point.y - (frame.y + frame.height / 2)) / scale,
  };
}

/**
 * Zoom around a sheet-space cursor, preserving the local point beneath it.
 */
export function zoomBlueprintCameraAt(
  camera: BlueprintCamera,
  frame: BlueprintFrame,
  cursor: StudioPoint2D,
  factor: number,
  limits: { minMetresPerPixel: number; maxMetresPerPixel: number },
): BlueprintCamera {
  const safeFactor = Number.isFinite(factor) && factor > 0 ? factor : 1;
  const minScale = Math.max(1e-9, limits.minMetresPerPixel);
  const maxScale = Math.max(minScale, limits.maxMetresPerPixel);
  const nextMetresPerPixel = Math.min(
    maxScale,
    Math.max(minScale, camera.metresPerPixel / safeFactor),
  );
  if (nextMetresPerPixel === camera.metresPerPixel) return camera;

  const localUnderCursor = blueprintToLocal(cursor, camera, frame);
  const nextScale = 1 / nextMetresPerPixel;
  const frameCenterX = frame.x + frame.width / 2;
  const frameCenterY = frame.y + frame.height / 2;
  return {
    centerXM: localUnderCursor.xM - (cursor.x - frameCenterX) / nextScale,
    centerYM: localUnderCursor.yM + (cursor.y - frameCenterY) / nextScale,
    metresPerPixel: nextMetresPerPixel,
  };
}

export function panBlueprintCamera(
  camera: BlueprintCamera,
  frame: BlueprintFrame,
  deltaSheetX: number,
  deltaSheetY: number,
): BlueprintCamera {
  const scale = blueprintScale(camera, frame);
  return {
    ...camera,
    centerXM: camera.centerXM - deltaSheetX / scale,
    centerYM: camera.centerYM + deltaSheetY / scale,
  };
}

/** Choose a readable 1–2–5 scale-bar distance at roughly the target span. */
export function niceScaleDistance(targetM: number): number {
  if (!Number.isFinite(targetM) || targetM <= 0) return 1;
  const exponent = Math.floor(Math.log10(targetM));
  const power = 10 ** exponent;
  const normalized = targetM / power;
  const multiplier = normalized >= 5 ? 5 : normalized >= 2 ? 2 : 1;
  return multiplier * power;
}

export function mapPointsForTarget(
  testFit: GenerationParcelTestFitResult,
  target: StudioFitTarget,
  selectedId: string | null,
): Array<{ lat: number; lng: number }> {
  const parcel = testFit.parcel?.polygon ?? [];
  const components = testFit.components.flatMap(component => component.coordinates);
  const layout = [...parcel, ...components];
  if (target === 'target-parcel') return parcel.length > 0 ? [...parcel] : layout;
  if (target === 'selection') {
    const selected = testFit.components.find(
      component => component.rectangle.id === selectedId,
    );
    return selected?.coordinates.length ? [...selected.coordinates] : layout;
  }
  if (target === 'issues') {
    const issues = testFit.components
      .filter(isStudioFitIssueComponent)
      .flatMap(component => component.coordinates);
    return issues.length > 0 ? issues : layout;
  }
  return layout.length > 0 ? layout : [testFit.anchor];
}

export function isValidBlueprintCamera(value: unknown): value is BlueprintCamera {
  if (!value || typeof value !== 'object') return false;
  const camera = value as Partial<BlueprintCamera>;
  return Number.isFinite(camera.centerXM)
    && Number.isFinite(camera.centerYM)
    && Number.isFinite(camera.metresPerPixel)
    && (camera.metresPerPixel ?? 0) > 0
    && (camera.metresPerPixel ?? 0) <= 1_000_000;
}

export function isValidStudioMapCamera(value: unknown): value is StudioMapCamera {
  if (!value || typeof value !== 'object') return false;
  const camera = value as Partial<StudioMapCamera>;
  return Boolean(camera.center)
    && Number.isFinite(camera.center?.lat)
    && Number.isFinite(camera.center?.lng)
    && (camera.center?.lat ?? 91) >= -90
    && (camera.center?.lat ?? -91) <= 90
    && (camera.center?.lng ?? 181) >= -180
    && (camera.center?.lng ?? -181) <= 180
    && Number.isFinite(camera.zoom)
    && (camera.zoom ?? -1) >= 0
    && (camera.zoom ?? 25) <= 24;
}
